import { useState, useRef, useCallback, useEffect } from 'react';
import { OrgData, Process, data } from '../data';

const STORAGE_KEY = 'pedbot-org-data';

export type UpdatePerson   = (personId: string, field: 'name' | 'level', value: string) => void;
export type AddProcess     = (tribeId: string, process: Process) => void;
export type UpdateProcess  = (tribeId: string, processId: string, updates: Partial<Omit<Process, 'id'>>) => void;
export type DeleteProcess  = (tribeId: string, processId: string) => void;
export type UpdateKR       = (tribeId: string, objectiveId: string, krId: string, current: number) => void;

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

function migrateData(loaded: OrgData): OrgData {
  const clone = deepClone(loaded);
  if (!clone.cycle) clone.cycle = data.cycle;
  for (const tribe of clone.tribes) {
    if (!tribe.okrs) {
      const defaultTribe = data.tribes.find(t => t.id === tribe.id);
      tribe.okrs = deepClone(defaultTribe?.okrs ?? []);
    }
  }
  return clone;
}

function loadInitialData(): OrgData {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return migrateData(JSON.parse(saved) as OrgData);
  } catch {}
  return deepClone(data);
}

function updatePersonInOrg(
  org: OrgData,
  personId: string,
  field: 'name' | 'level',
  value: string
): OrgData {
  const clone = deepClone(org);

  if (clone.cto.id === personId) {
    clone.cto = { ...clone.cto, [field]: value };
    return clone;
  }
  if (clone.coordinator.id === personId) {
    clone.coordinator = { ...clone.coordinator, [field]: value };
    return clone;
  }

  for (const tribe of clone.tribes) {
    for (const group of ['devs', 'po', 'analistas'] as const) {
      const idx = tribe.members[group].findIndex(p => p.id === personId);
      if (idx !== -1) {
        tribe.members[group][idx] = { ...tribe.members[group][idx], [field]: value };
        return clone;
      }
    }
  }

  return clone;
}

export function useOrgState() {
  const [orgData, setOrgData] = useState<OrgData>(loadInitialData);
  const [isDirty, setIsDirty] = useState(false);
  const orgDataRef = useRef(orgData);

  useEffect(() => {
    orgDataRef.current = orgData;
  }, [orgData]);

  const updatePerson = useCallback<UpdatePerson>((personId, field, value) => {
    setOrgData(prev => updatePersonInOrg(prev, personId, field, value));
    setIsDirty(true);
  }, []);

  const addProcess = useCallback<AddProcess>((tribeId, process) => {
    setOrgData(prev => {
      const clone = deepClone(prev);
      const tribe = clone.tribes.find(t => t.id === tribeId);
      if (tribe) tribe.processes.push(process);
      return clone;
    });
    setIsDirty(true);
  }, []);

  const updateProcess = useCallback<UpdateProcess>((tribeId, processId, updates) => {
    setOrgData(prev => {
      const clone = deepClone(prev);
      const tribe = clone.tribes.find(t => t.id === tribeId);
      if (!tribe) return clone;
      const idx = tribe.processes.findIndex(p => p.id === processId);
      if (idx !== -1) tribe.processes[idx] = { ...tribe.processes[idx], ...updates };
      return clone;
    });
    setIsDirty(true);
  }, []);

  const deleteProcess = useCallback<DeleteProcess>((tribeId, processId) => {
    setOrgData(prev => {
      const clone = deepClone(prev);
      const tribe = clone.tribes.find(t => t.id === tribeId);
      if (tribe) tribe.processes = tribe.processes.filter(p => p.id !== processId);
      return clone;
    });
    setIsDirty(true);
  }, []);

  const updateKR = useCallback<UpdateKR>((tribeId, objectiveId, krId, current) => {
    setOrgData(prev => {
      const clone = deepClone(prev);
      const tribe = clone.tribes.find(t => t.id === tribeId);
      if (!tribe) return clone;
      const obj = tribe.okrs.find(o => o.id === objectiveId);
      if (!obj) return clone;
      const kr = obj.keyResults.find(k => k.id === krId);
      if (kr) kr.current = current;
      return clone;
    });
    setIsDirty(true);
  }, []);

  const save = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(orgDataRef.current));
    setIsDirty(false);
  }, []);

  const discard = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setOrgData(deepClone(data));
    setIsDirty(false);
  }, []);

  return { orgData, isDirty, updatePerson, addProcess, updateProcess, deleteProcess, updateKR, save, discard };
}
